// frontend/src/hooks/usePyodide.ts
//
// Kod çalıştırma için Pyodide (WebAssembly üzerinde gerçek bir CPython
// derlemesi) kullanılır. Bu, backend'de `eval`/`exec` çalıştırmaktan
// KASITLI olarak kaçınmak için bir mimari tercihtir: kullanıcı girdisini
// sunucu tarafında çalıştırmak, rastgele kod çalıştırma (RCE) riski taşır.
// Pyodide tamamen tarayıcı sekmesi içinde, sandboxed bir WASM ortamında
// çalışır — en kötü ihtimalle kullanıcı kendi sekmesini kilitler, sunucuya
// veya diğer kullanıcılara hiçbir risk oluşturmaz.
//
// Pyodide ilk yüklemede ~10-15MB indirir ve birkaç saniye sürebilir;
// bu yüzden `isLoading` durumu UI'da gösterilmeli.

import { useEffect, useRef, useState, useCallback } from 'react';

declare global {
  interface Window {
    loadPyodide?: (config?: { indexURL?: string }) => Promise<PyodideInterface>;
  }
}

interface PyodideInterface {
  runPythonAsync: (code: string) => Promise<unknown>;
  setStdout: (options: { batched: (output: string) => void }) => void;
  setStderr: (options: { batched: (output: string) => void }) => void;
}

const PYODIDE_CDN = 'https://cdn.jsdelivr.net/pyodide/v0.26.4/full/';

let pyodideSingleton: PyodideInterface | null = null;
let loadingPromise: Promise<PyodideInterface> | null = null;

function loadPyodideScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.loadPyodide) return resolve();
    const script = document.createElement('script');
    script.src = `${PYODIDE_CDN}pyodide.js`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Pyodide script yüklenemedi.'));
    document.head.appendChild(script);
  });
}

async function getPyodide(): Promise<PyodideInterface> {
  if (pyodideSingleton) return pyodideSingleton;
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    await loadPyodideScript();
    const instance = await window.loadPyodide!({ indexURL: PYODIDE_CDN });
    pyodideSingleton = instance;
    return instance;
  })();

  return loadingPromise;
}

export interface RunResult {
  output: string;
  error: string | null;
  durationMs: number;
}

export function usePyodide() {
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const pyodideRef = useRef<PyodideInterface | null>(null);

  useEffect(() => {
    let cancelled = false;

    getPyodide()
      .then((instance) => {
        if (cancelled) return;
        pyodideRef.current = instance;
        setIsReady(true);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Python ortamı yüklenemedi.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const runPython = useCallback(async (code: string): Promise<RunResult> => {
    const pyodide = pyodideRef.current;
    if (!pyodide) {
      return { output: '', error: 'Python ortamı henüz hazır değil.', durationMs: 0 };
    }

    let collectedOutput = '';
    let collectedError = '';
    pyodide.setStdout({ batched: (s) => (collectedOutput += s + '\n') });
    pyodide.setStderr({ batched: (s) => (collectedError += s + '\n') });

    const startedAt = performance.now();
    try {
      // Not: Pyodide ana thread'de senkron çalışır. Sonsuz döngü içeren kod
      // (örn. `while True: pass`) sekmeyi tamamen kilitler — Promise.race
      // ile bunu "iptal edemeyiz", yalnızca UI'da bir zaman aşımı uyarısı
      // gösterebiliriz (gerçek izolasyon için Pyodide bir Web Worker'a
      // taşınmalı — bu, ileride bir performans iyileştirmesi olarak yapılabilir).
      const timeoutWarning = setTimeout(() => {
        console.warn('Kod 8 saniyeden uzun sürüyor — sonsuz döngü olabilir.');
      }, 8000);

      await pyodide.runPythonAsync(code);
      clearTimeout(timeoutWarning);

      return {
        output: collectedOutput.trimEnd(),
        error: collectedError.trim() || null,
        durationMs: Math.round(performance.now() - startedAt),
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return {
        output: collectedOutput.trimEnd(),
        error: message,
        durationMs: Math.round(performance.now() - startedAt),
      };
    }
  }, []);

  return { isReady, isLoading, loadError, runPython };
}
